import { useEffect, useState } from "react";
import { Route, Routes, BrowserRouter, Navigate } from "react-router-dom";
import Dashboard from "components/layouts";
import Login from "components/Auth/SignInForm";
import ForgotPasswordForm from "components/Auth/ForgotPasswordForm";
import VerifyEmail from "components/Auth/VerifyEmail";
import { BACKEND_URL, BASE_PATH } from "utils/env";
import routeSchema from "./backendRoutes.json";
import VendorSignUpForm from "components/Auth/VendorSignUpForm";
import { Flex, Spinner } from "@chakra-ui/react";
import CreateOrganisation from "App/WorkFlow/component/CreateOrganisation/CreateOrganisation";
import ResetPasswordForm from "components/Auth/ResetPassword";
import MicrosoftCallback from "components/Auth/microsoftLoginpage/MicrosoftCallBack";

const server = BACKEND_URL;

interface RouteObject {
  name: string;
  path: string;
  element: React.ReactElement;
  isProtected: boolean;
}

const routes: RouteObject[] = [
  {
    name: "Dashboard",
    path: "/",
    element: <Dashboard />,
    isProtected: true,
  },
  {
    name: "Login",
    path: "/login",
    element: <Login />,
    isProtected: false,
  },
  {
    name: "Forgot Password",
    path: "/forgot-password",
    element: <ForgotPasswordForm />,
    isProtected: false,
  },
  {
    name: "resetPassword",
    path: "/auth/reset-password",
    element: <ResetPasswordForm />,
    isProtected: false,
  },
  {
    name: "callback",
    path: "/auth/callback",
    element: <MicrosoftCallback />,
    isProtected: false,
  },
  {
    name: "Verify Email",
    path: "/verify-email",
    element: <VerifyEmail />,
    isProtected: false,
  },
  {
    name: "vendorSignup",
    path: "/vendorSignup",
    element: <VendorSignUpForm />,
    isProtected: false,
  },
  {
    name: "vendorSignup",
    path: "/createOrganisation",
    element: <CreateOrganisation type="Add" />,
    isProtected: false,
  },
];

const Router = () => {
  const [isLoggedIn, setisLoggedIn] = useState<any>(null);
  const [, setIsLoading] = useState(true);

  const checkUserLoginStatus = async () => {
    try {
      const response: any = await appRequest("user", "isLoggedIn");
      if (response === true) {
        setisLoggedIn(true);
      } else {
        setisLoggedIn(false);
      }
    } catch (err) {
      setisLoggedIn(false);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    checkUserLoginStatus();
  }, []);

  if (isLoggedIn === null) {
    // Render a loading state until the login status is determined
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        p={6}
        ps={12}
        pe={12}
        minH="calc(100vh - 100px)"
      >
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="teal.500"
          size="xl"
        />
      </Flex>
    );
  }

  // Fetch the token from local storage and send a loggedIn request to the backend to check if the token is valid
  return (
    <BrowserRouter>
      <Routes>
        {routes.map((route) => {
          const { path, element, isProtected } = route;
          const isAuthorized = !isProtected || isLoggedIn;
          return (
            <Route
              key={path}
              path={path}
              element={
                isAuthorized ? (
                  isLoggedIn && path === "/login" ? (
                    <Navigate to="/" />
                  ) : (
                    element
                  )
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          );
        })}
      </Routes>
    </BrowserRouter>
  );
};

interface RouteSchema {
  [key: string]: {
    [key: string]: {
      url: string;
      method: string;
      body?: any;
      headers?: any;
      query?: object;
    };
  };
}

const typedRouteSchema: RouteSchema = routeSchema;

const getValidToken = async () => {
  let token = sessionStorage.getItem("auth-token");
  const expiry = Number(sessionStorage.getItem("auth-token-expiry") || 0);

  if (!token) return null;

  // testing time
  return token;
  // Refresh if expired or about to expire (1 min buffer)
  if (Date.now() > expiry - 60 * 1000) {
    try {
      const refreshRes = await fetch(`${BACKEND_URL}/auth/refresh-microsoft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ deviceInfo: "default" }),
      });
      const data: any = await refreshRes.json();
      if (data.status === "success") {
        let token = data.token;
        sessionStorage.setItem("auth-token", token);
        sessionStorage.setItem(
          "auth-token-expiry",
          (Date.now() + data.expiresIn * 1000).toString()
        );
      } else {
        localStorage.removeItem("auth-token");
        localStorage.removeItem("auth-token-expiry");
        window.location.href = BASE_PATH;
        return null;
      }
    } catch (err) {
      window.location.href = BASE_PATH;
      console.error("Failed to refresh Microsoft token", err);
      return null;
    }
  }
  return token;
};

export async function appRequest(
  type: string,
  requestType: string,
  inputs?: any
) {
  return new Promise(async (resolve, reject) => {
    try {
      let request = typedRouteSchema[type][requestType];
      let { url, method, headers, body, query } = request;
      if (headers) {
        if (headers["Authorization"]) {
          const token = await getValidToken();
          if (!token) throw new Error("User not authenticated");
          headers["Authorization"] = "Bearer " + token;
        }
      }

      let id;
      let response;
      switch (method) {
        case "GET":
          let queries = "";
          if (query) {
            if (inputs) {
              queries = "";
              await Object.entries(inputs).forEach(([key, value]) => {
                queries += `${key}=${value}&`;
              });
            } else {
              reject("No inputs provided");
            }
          }
          response = await getRequest(url, headers, queries);
          break;
        case "POST":
          if (!body) {
            body = {};
          } else if (inputs) {
            body = inputs;
          } else {
            reject("No inputs provided");
          }
          response = await postRequest(url, headers, body);
          break;
        case "PUT":
          if (!body) {
            body = {};
          } else if (inputs && inputs.id) {
            id = inputs.id;
            delete inputs.id;
            body = inputs;
          } else {
            reject("No inputs or id provided");
          }
          response = await putRequest(url, headers, id, body);
          break;
        case "DELETE":
          if (inputs && inputs.id) {
            id = inputs.id;
          } else {
            reject("No id provided");
          }
          response = await deleteRequest(url, headers, id);
          break;
        default:
          reject("Invalid request method");
      }
      resolve(response);
    } catch (err) {
      reject(err);
    }
  });
}

// export function postRequest(url: string, headers: any, body: any) {
//   return new Promise(async (resolve, reject) => {
//     const objectToFormData = (
//       obj: any,
//       formData: FormData,
//       parentKey?: string
//     ) => {
//       if (typeof obj !== "object" || obj === null) {
//         formData.append(parentKey || "", obj);
//       } else {
//         Object.keys(obj).forEach((key) => {
//           const value = obj[key];
//           const nestedKey = parentKey ? `${parentKey}.${key}` : key;
//           objectToFormData(value, formData, nestedKey);
//         });
//       }
//     };

//     const formData = new FormData();
//     objectToFormData(body, formData);

//     const requestOptions: RequestInit = {
//       method: "POST",
//       headers: headers,
//       body: JSON.stringify(body),
//     };
//     try {
//       const response = await fetch(`${server}/${url}`, requestOptions);
//       resolve(response.json());
//     } catch (err) {
//       reject(err);
//     }
//   });
// }

export function postRequest(
  url: string,
  headers: any,
  body: any,
  timeout: number = 900000 // 15 minutes (900000 ms)
) {
  return new Promise(async (resolve, reject) => {
    const controller = new AbortController();

    const timer = setTimeout(() => {
      controller.abort();
    }, timeout);

    const requestOptions: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    };

    try {
      const response = await fetch(`${server}/${url}`, requestOptions);
      clearTimeout(timer);

      const data = await response.json();
      resolve(data);
    } catch (err: any) {
      clearTimeout(timer);

      if (err.name === "AbortError") {
        reject(new Error("Request timeout exceeded (15 minutes)"));
      } else {
        reject(err);
      }
    }
  });
}


// export function getRequest(url: string, headers: any, query: string) {
//   return new Promise(async (resolve, reject) => {
//     const requestOptions = {
//       method: "GET",
//       headers: headers,
//     };
//     try {
//       if (query) query = "?" + query || "";
//       const response = await fetch(`${server}/${url}${query}`, requestOptions);
//       resolve(response.json());
//     } catch (err) {
//       reject(err);
//     }
//   });
// }

export function getRequest(
  url: string,
  headers: any,
  query: string,
  timeout: number = 900000 // 15 minutes
) {
  return new Promise(async (resolve, reject) => {
    const controller = new AbortController();

    const timer = setTimeout(() => {
      controller.abort();
    }, timeout);

    const requestOptions: RequestInit = {
      method: "GET",
      headers: headers,
      signal: controller.signal,
    };

    try {
      const finalQuery = query ? `?${query}` : "";
      const response = await fetch(
        `${server}/${url}${finalQuery}`,
        requestOptions
      );

      clearTimeout(timer);
      resolve(response.json());
    } catch (err: any) {
      clearTimeout(timer);

      if (err.name === "AbortError") {
        reject(new Error("GET request timeout exceeded (15 minutes)"));
      } else {
        reject(err);
      }
    }
  });
}


// export function putRequest(url: string, headers: any, id: string, data: any) {
//   return new Promise(async (resolve, reject) => {
//     const requestOptions = {
//       method: "PUT",
//       headers: headers,
//       body: JSON.stringify(data),
//     };
//     try {
//       const response = await fetch(`${server}/${url}/${id}`, requestOptions);
//       resolve(response.json());
//     } catch (err) {
//       reject(err);
//     }
//   });
// }

export function putRequest(
  url: string,
  headers: any,
  id: string,
  data: any,
  timeout: number = 900000 // 15 minutes
) {
  return new Promise(async (resolve, reject) => {
    const controller = new AbortController();

    const timer = setTimeout(() => {
      controller.abort();
    }, timeout);

    const requestOptions: RequestInit = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(data),
      signal: controller.signal,
    };

    try {
      const response = await fetch(
        `${server}/${url}/${id}`,
        requestOptions
      );

      clearTimeout(timer);
      resolve(response.json());
    } catch (err: any) {
      clearTimeout(timer);

      if (err.name === "AbortError") {
        reject(new Error("PUT request timeout exceeded (15 minutes)"));
      } else {
        reject(err);
      }
    }
  });
}


export function deleteRequest(url: string, headers: any, id: string) {
  return new Promise(async (resolve, reject) => {
    const requestOptions = {
      method: "DELETE",
      headers: headers,
    };
    try {
      const response = await fetch(`${server}/${url}/${id}`, requestOptions);
      resolve(response.json());
    } catch (err) {
      reject(err);
    }
  });
}

export default Router;